import useUserAccountSettings from "../hooks/useUserAccountSettings";
import { Button, Col, Form, FormSelect, Row, Spinner } from "react-bootstrap";
import { defineMessages, injectIntl } from "react-intl";
import PropTypes from "prop-types";
import useForm from "../hooks/useForm";
import React, { useEffect, useState } from "react";
import useBankAccount from "../hooks/useBankAccount";
import { useDebouncedCallback } from "use-debounce";
import useFormatter from "../hooks/useFormatter";

const messages = defineMessages({
    bankAccountDetails: {
        id: "Pankkitilin tiedot",
        defaultMessage: "Pankkitilin tiedot"
    },
    updateInfo: {
        id: "Päivitä tiedot",
        defaultMessage: "Päivitä tiedot"
    },
    accountType: {
        id: "Tilityyppi",
        defaultMessage: "Tilityyppi"
    },
    accountNumber: {
        id: "Tilinumero",
        defaultMessage: "Tilinumero"
    },
    accountHolderName: {
        id: "Tilinumeron haltijan nimi",
        defaultMessage: "Tilinumeron haltijan nimi"
    },
    bic: {
        id: "BIC",
        defaultMessage: "BIC"
    },
    iban: {
        id: "IBAN",
        defaultMessage: "IBAN"
    },
    bban: {
        id: "BBAN (Tilinumero)",
        defaultMessage: "BBAN (Tilinumero)"
    },
    bankgiro: {
        id: "BankGiro",
        defaultMessage: "BankGiro"
    },
    plusgirot: {
        id: "PlusGirot",
        defaultMessage: "PlusGirot"
    },
    lastUpdated: {
        id: "Viimeksi päivitetty",
        defaultMessage: "Viimeksi päivitetty"
    }
});

const SettingsBankAccount = (props) => {
    const { settings } = useUserAccountSettings();
    const { dirty, form, handleChange, validateForm, updateForm, sanitizeForm } = useForm(settings);
    const { validateBankAccount } = useBankAccount();
    const [ bicDisabled, setBicDisabled ] = useState(false);
    const [ accountNumberIsValid, setAccountNumberIsValid ] = useState(false);
    const { formatDateTime } = useFormatter();

    const lng = props.intl.formatMessage;

    const debouncedAccountCheck = useDebouncedCallback((accountType, accountNumber) => {
        validateBankAccount(accountType, accountNumber).then(result => {
            setAccountNumberIsValid(result?.is_valid ?? false);
            if (result?.is_valid && result?.bic !== null && result?.bic !== undefined && result?.bic !== '') {
                setBicDisabled(true);
                updateForm({ ...form, bankAccountBic: result?.bic });
            } else if (accountType !== 'iban') {
                setBicDisabled(true);
                updateForm({ ...form, bankAccountBic: '' });
            } else {
                setBicDisabled(false);
            }
        })}, 500);

    useEffect(() => {
        if (props.loading || form.bankAccountType === undefined) {
            return; // Don't validate while loading
        }
        if (form.bankAccountType === '') {
            updateForm({ ...form, bankAccountNumber: '', bankAccountBic: '', bankAccountHolderName: '' });
            setBicDisabled(true);
            setAccountNumberIsValid(false);
        } else {
            debouncedAccountCheck(form.bankAccountType, form.bankAccountNumber);
        }
    }, [form.bankAccountType, form.bankAccountNumber, props.loading]);
    
    const handleSubmit = (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (validateForm(event.target)) {
            props.onSubmit(sanitizeForm());
        }
    }

    const shouldValidateAccountNumber = ['iban', 'bban'].includes(form.bankAccountType);
    const submitIsEnabled = dirty &&
        !props.loading &&
        !(shouldValidateAccountNumber && !accountNumberIsValid);

    if (props.accountTypes.length === 0) {
        return <Spinner animation="border" size="sm" />;
    }

    return (
        <div>
            <h2>{lng(messages.bankAccountDetails)}</h2>
            <Form id='settings-bank-account-form' onSubmit={handleSubmit} noValidate>
                <Form.Floating className="mb-2 pb-2">
                    <FormSelect
                        name='bankAccountType'
                        className="form-control"
                        onChange={handleChange}
                        value={ form.bankAccountType }
                    >
                        {['', ...props.accountTypes].map((option) => (
                            <option key={option} value={option}>
                                {option !== '' ? lng(messages[option]): '-'}
                            </option>
                        ))}
                    </FormSelect>
                    <Form.Label htmlFor="bankAccountType">{lng(messages.accountType)}</Form.Label>
                </Form.Floating>
                <Form.Floating className="mb-2 pb-2">
                    <Form.Control
                        required={form.bankAccountType !== ''}
                        name='bankAccountNumber'
                        value={form.bankAccountNumber}
                        isValid={accountNumberIsValid}
                        isInvalid={shouldValidateAccountNumber && form.bankAccountNumber !== '' && !accountNumberIsValid}
                        onChange={handleChange}
                        disabled={!form.bankAccountType || form.bankAccountType === ''}
                    />
                    <Form.Label htmlFor="bankAccountNumber">{lng(messages.accountNumber)}</Form.Label>
                </Form.Floating>
                <Form.Floating className="mb-2 pb-2">
                    <Form.Control
                        name='bankAccountBic'
                        value={form.bankAccountBic}
                        onChange={handleChange}
                        disabled={bicDisabled}
                        required={form.bankAccountType === 'iban'}
                    />
                    <Form.Label htmlFor="bankAccountBic">{lng(messages.bic)}</Form.Label>
                </Form.Floating>
                <Form.Floating className="mb-2 pb-2">
                    <Form.Control
                        required={form.bankAccountType !== ''}
                        name='bankAccountHolderName'
                        value={form.bankAccountHolderName}
                        onChange={handleChange}
                        disabled={!form.bankAccountType || form.bankAccountType === ''}
                    />
                    <Form.Label htmlFor="bankAccountHolderName">{lng(messages.accountHolderName)}</Form.Label>
                </Form.Floating>
                <Row>
                    <Col lg={6}>
                        <Button
                            form="settings-bank-account-form"
                            variant="primary"
                            type="submit"
                            disabled={!submitIsEnabled}
                        >
                            {props.loading && (<Spinner animation="border" size="sm" />)} {lng(messages.updateInfo)}
                        </Button>
                    </Col>
                    <Col lg={6} className='mt-2 small text-lg-end'>
                        {form.updatedAt && form.updatedAt !== '' && (
                            <>
                                <span className='text-light text-nowrap'>{lng(messages.lastUpdated)}:&nbsp;</span>
                                <span className='text-light text-nowrap'>{formatDateTime(form.updatedAt)}</span>
                            </>
                        )}
                    </Col>
                </Row>
            </Form>
        </div>
    )
}

SettingsBankAccount.propTypes = {
    'intl': PropTypes.object.isRequired,
    'loading': PropTypes.bool.isRequired,
    'onSubmit': PropTypes.func.isRequired,
    'accountTypes': PropTypes.arrayOf(PropTypes.string).isRequired
};

export default injectIntl(SettingsBankAccount);
